import React from 'react'
import { Observable } from 'rx'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import type { BetSlipResponse, Selection, ToteSelection } from '@mobi/betslip/types'
import {
  isMysteryComboBetType,
  isMysterySelection,
  isNoveltyBetType,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { state$ as receiptState$ } from './driver'
import { QuickbetReceipt, BetSlipReceipt, KambiSportsReceipt } from './Components'

const quickbetAndReceiptState$ = Observable.combineLatest(
  quickbetState$,
  receiptState$,
  (quickbetState, receiptState) => {
    const quickbet: QuickbetState = quickbetState.toJS()
    return {
      betPlaced: quickbet.betPlaced,
      selection: quickbet.selection,
      selectionDetails: quickbet.selectionDetails,
      receiptState,
    }
  }
)

export const Receipt: React.ComponentClass = observeImmutable(
  quickbetAndReceiptState$,
  ({ record }) => {
    if (!record.betPlaced) {
      return null
    }

    if (record.receiptState.betslipReceipt) {
      return <BetSlipReceipt responses={record.receiptState.betslipReceipt as BetSlipResponse[]} />
    }

    if (record.receiptState.kambiCoupon) {
      return <KambiSportsReceipt coupon={record.receiptState.kambiCoupon as KambiHistoryCoupon} />
    }

    const { selection, selectionDetails, receiptState } = record
    const isMysteryBet = isMysterySelection(selection as Selection)

    return (
      <QuickbetReceipt
        isToteBet={isToteSelection(selection as Selection)}
        isMysteryBet={isMysteryBet}
        isMysteryCombo={isMysteryBet && isMysteryComboBetType(selection as Selection)}
        isNoveltyBet={isNoveltyBetType((selection as ToteSelection).betType)}
        accountBalance={receiptState.accountBalance}
        ticketNumber={receiptState.ticketNumber}
        betCost={receiptState.betCost}
        winInvestment={receiptState.winInvestment}
        placeInvestment={receiptState.placeInvestment}
        projectedWinPayout={receiptState.projectedWinPayout}
        projectedPlacePayout={receiptState.projectedPlacePayout}
        specialOffers={receiptState.specialOffers}
        hasSpecialOffer={receiptState.hasSpecialOffer}
        divXAnnotations={receiptState.divXAnnotations}
        campaignActivatedInd={receiptState.campaignActivatedInd}
        campaignType={receiptState.campaignType}
        bonusBet={receiptState.bonusBet}
        amountPaid={receiptState.amountPaid}
        showBalance={false}
        selectionDetails={selectionDetails}
      />
    )
  }
)

Receipt.displayName = 'Receipt'
